import type { IGfpaFactorContributionFreq, IGfpaFactorContributionTotal } from '@/interfaces/gfpaReport.interface'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

export interface FactorAttributionPoint {
  portfolio: number | null
  benchmark: number | null
  active: number | null
}

export interface FactorAttributionSeries {
  name: string
  data: FactorAttributionPoint[]
}

export interface FactorAttributionChartData {
  categories: string[]
  series: FactorAttributionSeries[]
}

export type FactorFrequency = 'D' | 'W' | 'M' | 'Y'

// Drilled level caps the legend at the 9 largest sub-factors plus a rolled-up "Other" bucket (10 total).
const TOP_DRILL_GROUPS = 9
export const OTHER_GROUP_NAME = 'Other'

// Add a nullable value to a running total, keeping null until at least one real value is seen.
const addNullable = (acc: number | null, v: number | null | undefined) => (v == null ? acc : (acc ?? 0) + v)

// Some backend rows omit a factor category (or send it blank) despite the string type. Collapse those
// to a stable label so grouping/keys/sorts stay defined and non-empty.
const UNKNOWN_FACTOR_LABEL = 'Unknown'
const factorLabel = (v: string | null | undefined): string => v?.trim() || UNKNOWN_FACTOR_LABEL

// Preferred factor display order (prefix-stripped names, matched via orderRank); anything not listed
// falls to the end (backend sort).
const FACTOR_ORDER = [
  'Region',
  'Country',
  'Fundamental',
  'Industry',
  'Risk Free Rate',
  'Yield Return',
  'Duration',
  'Term Structure',
  'Yield Curve',
  'Sector',
  'Quality',
  'Other Spread',
  'Currency',
  'Currency Interaction',
]
// Selection sorts after the factors (factors + selection = total return).
const LAST_FACTORS = new Set(['Selection', 'Selection Residual'])

// Explicit order for a drilled factor's children (names must match the backend values).
const CHILD_ORDER: Record<string, string[]> = {
  'Yield Curve': ['(D1) Level', '(D2) Slope', '(D3) Curvature'],
  Quality: ['Aa', 'A', 'Baa', 'Ba', 'B', 'Caa'],
}
// Parents whose children just go alphabetical.
const ALPHABETICAL_CHILD_PARENTS = new Set(['Sector', 'Other Spread'])
// Drop the asset-class prefix the backend adds to factor names, e.g. "Equity Region" -> "Region".
// Accepts null/undefined: some backend rows omit the category.
export const stripFactorPrefix = (name: string | null | undefined) => (name ?? '').replace(/^(Equity|FixInc)\s+/i, '')

// Display name for a factor: strip the asset-class prefix and show Fundamental as Style.
export const factorDisplayName = (name: string) => {
  const clean = stripFactorPrefix(name)
  return clean === 'Fundamental' ? 'Style' : clean
}

// The factor hierarchy columns, top level first. Deepest grouping (a full drill) is factor_category_4.
type FactorHierarchyRow = Pick<
  IGfpaFactorContributionTotal,
  'factor_category_1' | 'factor_category_2' | 'factor_category_3' | 'factor_category_4'
>
const factorLevels = (r: FactorHierarchyRow): (string | null)[] => [
  r.factor_category_1,
  r.factor_category_2,
  r.factor_category_3,
  r.factor_category_4,
]

// Deepest drill supported: index into factorLevels (3 = factor_category_4).
export const MAX_FACTOR_DRILL_DEPTH = 3

// Whether a row sits under the given drill path (each path entry matches its level, missing → Unknown).
export const matchesFactorDrillPath = (r: FactorHierarchyRow, path: string[]): boolean =>
  path.every((p, i) => factorLabel(factorLevels(r)[i]) === p)

// Whether a row actually has a (non-blank) factor at the given depth, i.e. a child to drill into.
export const factorHasLevel = (r: FactorHierarchyRow, depth: number): boolean =>
  !!factorLevels(r)[depth]?.trim()

// Group key at the given drill depth: the category at exactly that level, "Unknown" when it's null or
// blank. No walk-up — a row that terminates above this depth becomes the "Unknown" residual group
// (which keeps a drill summing to its parent), not a series named after the factor just clicked.
// Childless factors are kept non-drillable separately, via buildDrillableGroups.
export const factorGroupKeyAtDepth = (r: FactorHierarchyRow, depth: number): string =>
  factorLabel(factorLevels(r)[depth] ?? null)

const orderRank = (name: string) => {
  const clean = stripFactorPrefix(name)
  // Selection sorts last — after even the unlisted asset-class categories.
  if (LAST_FACTORS.has(clean)) return Number.MAX_SAFE_INTEGER
  const i = FACTOR_ORDER.indexOf(clean)
  // Unlisted factors (e.g. top-level asset classes) fall between the preferred order and selection.
  return i === -1 ? Number.MAX_SAFE_INTEGER - 1 : i
}

// Pick the display frequency from the selected range length so the default view stays readable.
export function pickFactorFrequency(begin: dayjs.Dayjs, end: dayjs.Dayjs): FactorFrequency {
  const months = end.diff(begin, 'month', true)
  if (months <= 1) return 'D'
  if (months <= 6) return 'W'
  if (months <= 24) return 'M'
  return 'Y'
}

function formatPeriodLabel(date: string, frequency: FactorFrequency): string {
  const d = dayjs.utc(date)
  if (frequency === 'Y') return d.format('YYYY')
  if (frequency === 'M') return d.format('MMM YYYY')
  return d.format('MM/DD/YYYY')
}

export interface FactorPeriod {
  begin: string
  end: string
}

// Ordered periods (one per x-category), each with its full span. Derived from the unfiltered rows so
// it matches buildFactorAttributionChart's x-axis whether or not a factor is drilled.
export function buildFactorPeriods(rows: IGfpaFactorContributionFreq[] | null | undefined): FactorPeriod[] {
  const endByBegin = new Map<string, string>()
  for (const r of rows ?? []) {
    const prev = endByBegin.get(r.begin_date)
    if (prev === undefined || r.end_date > prev) endByBegin.set(r.begin_date, r.end_date)
  }
  return [...endByBegin.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map((begin) => ({ begin, end: endByBegin.get(begin) as string }))
}

// Map a Highcharts category-axis selection (fractional indices) to the covered period date range.
// Category i spans [i-0.5, i+0.5], so include a period only when its centre falls inside the drag —
// a selection ending in the next bar's left half must not pull that whole bar in. Dates are parsed in
// UTC to stay consistent with the rest of the attribution date handling.
export function factorSelectionRange(
  periods: FactorPeriod[],
  min: number,
  max: number,
): { start: dayjs.Dayjs; end: dayjs.Dayjs } | null {
  if (periods.length === 0) return null
  const lo = Math.max(0, Math.ceil(min))
  const hi = Math.min(periods.length - 1, Math.floor(max))
  if (hi < lo) return null
  return { start: dayjs.utc(periods[lo].begin), end: dayjs.utc(periods[hi].end) }
}

// Stacked bar: one x-category per period, one series per factor group. Groups by factor_category_1 at
// the top level; each entry in drillPath descends one level (factor_category_2, _3, _4), filtering to
// the chosen factor at each step and grouping by the next level down.
export function buildFactorAttributionChart(
  rows: IGfpaFactorContributionFreq[] | null | undefined,
  frequency: FactorFrequency = 'D',
  drillPath: string[] = [],
  // Authoritative per-group totals (no-freq) for the current drill level. When provided, drives the
  // top-9 selection and the highest-to-lowest display order so both match the legend numbers.
  rankByGroup?: Map<string, number | null>,
): FactorAttributionChartData {
  const depth = drillPath.length
  const source = !rows ? [] : rows.filter((r) => matchesFactorDrillPath(r, drillPath))
  if (source.length === 0) return { categories: [], series: [] }

  // Rows that terminate at this depth (no factor at the level) fall into an "Unknown" residual group
  // (factorGroupKeyAtDepth returns it), so a drill still sums back to its parent instead of under-counting.
  const groupKey = (r: IGfpaFactorContributionFreq) => factorGroupKeyAtDepth(r, depth)

  // Date grid comes from the unfiltered rows so drilling keeps the parent view's x-axis (same dates).
  const dates = buildFactorPeriods(rows).map((p) => p.begin)
  const dateIndex = new Map(dates.map((d, i) => [d, i]))

  // No sort field on the new schema, so preserve the backend's row order (first appearance), with the
  // preferred order applied on top (orderRank is neutral for unlisted names, e.g. top-level classes).
  const firstSeen = new Map<string, number>()
  for (const r of source) {
    const k = groupKey(r)
    if (!firstSeen.has(k)) firstSeen.set(k, firstSeen.size)
  }
  const groups = [...firstSeen.keys()].sort(
    (a, b) => orderRank(a) - orderRank(b) || (firstSeen.get(a) ?? 0) - (firstSeen.get(b) ?? 0),
  )

  // Sum portfolio/benchmark/active by (period, group); missing periods stay null.
  const emptyPoint = (): FactorAttributionPoint => ({ portfolio: null, benchmark: null, active: null })
  const totals = new Map<string, FactorAttributionPoint[]>()
  for (const g of groups) totals.set(g, Array.from({ length: dates.length }, emptyPoint))
  for (const r of source) {
    const i = dateIndex.get(r.begin_date)
    if (i === undefined) continue
    const point = totals.get(groupKey(r))?.[i]
    if (!point) continue
    point.portfolio = addNullable(point.portfolio, r.portfolio_total_return)
    point.benchmark = addNullable(point.benchmark, r.benchmark_total_return)
    point.active = addNullable(point.active, r.active_total_return)
  }

  // Drilled level can have many sub-factors (e.g. Country). Keep the 9 largest by total contribution
  // and roll the rest into an "Other" series, so the legend never exceeds 10 items. Rank by the
  // authoritative no-freq totals when provided (whole-range, stable across periods), else fall back to
  // the summed per-period magnitude.
  // Only rank by the no-freq totals when they've actually loaded; an empty map would score every group
  // 0 and silently keep the first 9 in row order, so fall back to the per-period magnitude until then.
  const hasRank = !!rankByGroup && rankByGroup.size > 0
  const groupMagnitude = (g: string) =>
    hasRank
      ? Math.abs(rankByGroup!.get(g) ?? 0)
      : (totals.get(g) ?? []).reduce((sum, p) => sum + Math.abs(p.portfolio ?? 0), 0)

  let displayGroups = groups
  let otherData: FactorAttributionPoint[] | null = null
  if (depth > 0 && groups.length > TOP_DRILL_GROUPS + 1) {
    const kept = new Set([...groups].sort((a, b) => groupMagnitude(b) - groupMagnitude(a)).slice(0, TOP_DRILL_GROUPS))
    displayGroups = groups.filter((g) => kept.has(g))
    otherData = Array.from({ length: dates.length }, emptyPoint)
    for (const g of groups) {
      if (kept.has(g)) continue
      totals.get(g)?.forEach((p, i) => {
        const o = otherData![i]
        o.portfolio = addNullable(o.portfolio, p.portfolio)
        o.benchmark = addNullable(o.benchmark, p.benchmark)
        o.active = addNullable(o.active, p.active)
      })
    }
  }

  // Drilled order: explicit child order (Yield Curve, Quality) or the preferred factor order; ties
  // fall to alphabetical (Sector, Other Spread) or total return, highest to lowest (e.g. countries).
  if (depth > 0) {
    // Group keys are raw (possibly prefixed) — strip before matching the (prefix-free) order tables.
    const parent = stripFactorPrefix(drillPath[depth - 1])
    const childOrder = CHILD_ORDER[parent]
    const alphabetical = ALPHABETICAL_CHILD_PARENTS.has(parent)
    const rankOf = (g: string) => {
      if (!childOrder) return orderRank(g)
      const i = childOrder.indexOf(stripFactorPrefix(g))
      return i === -1 ? Number.MAX_SAFE_INTEGER : i
    }
    displayGroups = [...displayGroups].sort((a, b) => {
      // Alphabetical parents (Sector, Other Spread) sort purely by name — no preferred-order override.
      if (alphabetical) return stripFactorPrefix(a).localeCompare(stripFactorPrefix(b))
      return rankOf(a) - rankOf(b) || (hasRank ? (rankByGroup!.get(b) ?? 0) - (rankByGroup!.get(a) ?? 0) : 0)
    })
  }

  const series = displayGroups.map((g) => ({ name: g, data: totals.get(g) ?? [] }))
  if (otherData) series.push({ name: OTHER_GROUP_NAME, data: otherData })

  return {
    categories: dates.map((d) => formatPeriodLabel(d, frequency)),
    series,
  }
}

// Per-group portfolio totals at the current drill level, from the no-freq rows. Keyed by the same
// group names buildFactorAttributionChart produces, so it can both rank/sort the chart and label the
// legend. Empty until drilled.
export function buildFactorDrillTotals(
  rows: IGfpaFactorContributionTotal[] | null | undefined,
  drillPath: string[],
): Map<string, number | null> {
  const map = new Map<string, number | null>()
  if (drillPath.length === 0 || !rows) return map
  const depth = drillPath.length
  for (const r of rows) {
    if (!matchesFactorDrillPath(r, drillPath)) continue
    // Terminating rows fall into the "Unknown" residual group so legend totals reconcile with the parent.
    const key = factorGroupKeyAtDepth(r, depth)
    map.set(key, addNullable(map.get(key) ?? null, r.portfolio_total_return))
  }
  return map
}

// Groups at the current drill level that have a real child one level down (so drilling shows something).
// Built from whichever rows drive the bars, so drill-ability doesn't depend on a second response.
export function buildDrillableGroups(
  rows: IGfpaFactorContributionTotal[] | IGfpaFactorContributionFreq[] | null | undefined,
  drillPath: string[],
): Set<string> {
  const set = new Set<string>()
  if (!rows || drillPath.length >= MAX_FACTOR_DRILL_DEPTH) return set
  const depth = drillPath.length
  for (const r of rows) {
    if (!matchesFactorDrillPath(r, drillPath) || !factorHasLevel(r, depth + 1)) continue
    set.add(factorGroupKeyAtDepth(r, depth))
  }
  return set
}

export interface FactorAttributionTotal {
  name: string
  sort: number
  portfolio: number | null
  benchmark: number | null
  active: number | null
}

// Totals over the whole (visible) range for the persistent strip. Always the top-level factors
// (factor_category_1), regardless of any chart drill-down.
export function buildFactorAttributionTotals(
  rows: IGfpaFactorContributionTotal[] | null | undefined,
): FactorAttributionTotal[] {
  if (!rows || rows.length === 0) return []

  const byKey = new Map<string, FactorAttributionTotal>()
  for (const r of rows) {
    const category = factorLabel(r.factor_category_1)
    let t = byKey.get(category)
    if (!t) {
      // No sort field on the new schema: preserve backend row order (first appearance).
      t = {
        name: category,
        sort: byKey.size,
        portfolio: null,
        benchmark: null,
        active: null,
      }
      byKey.set(category, t)
    }
    t.portfolio = addNullable(t.portfolio, r.portfolio_total_return)
    t.benchmark = addNullable(t.benchmark, r.benchmark_total_return)
    t.active = addNullable(t.active, r.active_total_return)
  }
  return [...byKey.values()].sort(
    (a, b) => orderRank(a.name) - orderRank(b.name) || a.sort - b.sort || a.name.localeCompare(b.name),
  )
}

export interface FactorAttributionTreeRow {
  factorPath: string[]
  factorReturn: number | null
  portfolioReturn: number | null
  benchmarkReturn: number | null
  activeReturn: number | null
  portfolioExposure: number | null
  benchmarkExposure: number | null
  exposureDifference: number | null
}

// Reverse of factorDisplayName's Fundamental -> Style mapping, so display-name path segments match the
// backend-named order tables (FACTOR_ORDER / CHILD_ORDER). Prefixes are already stripped in factorPath.
const toBackendFactorName = (display: string): string => (display === 'Style' ? 'Fundamental' : display)

// Order two sibling factors under the given (display-name) parent, mirroring buildFactorAttributionChart:
// an explicit CHILD_ORDER, else alphabetical under ALPHABETICAL_CHILD_PARENTS, else the structural
// orderRank (Selection last). Returns 0 for structural ties; the caller breaks those with backend
// first-appearance order to keep the comparison total.
const compareFactorSiblings = (a: string, b: string, parentDisplay: string | null): number => {
  const parent = parentDisplay ? toBackendFactorName(parentDisplay) : null
  if (parent) {
    const childOrder = CHILD_ORDER[parent]
    if (childOrder) {
      const rank = (g: string) => {
        const i = childOrder.indexOf(toBackendFactorName(g))
        return i === -1 ? Number.MAX_SAFE_INTEGER : i
      }
      return rank(a) - rank(b)
    }
    if (ALPHABETICAL_CHILD_PARENTS.has(parent)) return a.localeCompare(b)
  }
  return orderRank(toBackendFactorName(a)) - orderRank(toBackendFactorName(b))
}

// One row per deepest leaf (through factor_category_4) with its full display-name path for ag-grid tree
// data. Contributions/exposures are additive; factor return is a rate (nulled on repeated paths).
export function buildFactorAttributionTreeRows(
  rows: IGfpaFactorContributionTotal[] | null | undefined,
): FactorAttributionTreeRow[] {
  if (!rows || rows.length === 0) return []
  const byPath = new Map<string, FactorAttributionTreeRow>()
  const pathCount = new Map<string, number>()
  // First-appearance order per group prefix (backend order), used as the ordering tiebreak so distinct
  // siblings never compare equal (keeps the sibling comparator total/transitive).
  const firstSeen = new Map<string, number>()
  let seq = 0
  for (const r of rows) {
    // Walk the hierarchy top-down, stopping at the first blank level: that's this row's leaf path.
    const factorPath: string[] = []
    for (const level of factorLevels(r)) {
      if (!level?.trim()) break
      factorPath.push(factorDisplayName(level))
    }
    if (factorPath.length === 0) continue
    const key = factorPath.join(' > ')
    for (let i = 0; i < factorPath.length; i++) {
      const prefix = factorPath.slice(0, i + 1).join(' > ')
      if (!firstSeen.has(prefix)) firstSeen.set(prefix, seq++)
    }
    pathCount.set(key, (pathCount.get(key) ?? 0) + 1)
    let row = byPath.get(key)
    if (!row) {
      row = {
        factorPath,
        factorReturn: null,
        portfolioReturn: null,
        benchmarkReturn: null,
        activeReturn: null,
        portfolioExposure: null,
        benchmarkExposure: null,
        exposureDifference: null,
      }
      byPath.set(key, row)
    }
    row.factorReturn = addNullable(row.factorReturn, r.factor_return)
    row.portfolioReturn = addNullable(row.portfolioReturn, r.portfolio_total_return)
    row.benchmarkReturn = addNullable(row.benchmarkReturn, r.benchmark_total_return)
    row.activeReturn = addNullable(row.activeReturn, r.active_total_return)
    row.portfolioExposure = addNullable(row.portfolioExposure, r.portfolio_weighted_exposure)
    row.benchmarkExposure = addNullable(row.benchmarkExposure, r.benchmark_weighted_exposure)
  }
  const keys = [...byPath.keys()]
  for (const [key, row] of byPath) {
    // A rate isn't additive — drop a repeated leaf's summed factor return.
    if ((pathCount.get(key) ?? 0) > 1) row.factorReturn = null
    // A path that prefixes a deeper sibling would become a group node and lose its own data; make it a leaf.
    if (keys.some((k) => k.startsWith(`${key} > `))) {
      row.factorPath = [...row.factorPath, UNKNOWN_FACTOR_LABEL]
    }
    if (row.portfolioExposure != null || row.benchmarkExposure != null) {
      row.exposureDifference = (row.portfolioExposure ?? 0) - (row.benchmarkExposure ?? 0)
    }
  }
  // Order factors/subfactors to match the chart: structural order (orderRank, Selection last), explicit
  // CHILD_ORDER where defined, alphabetical only under ALPHABETICAL_CHILD_PARENTS. Structural ties fall
  // back to backend first-appearance order, keeping the comparator total (distinct siblings never tie).
  const seenRank = (prefix: string) => firstSeen.get(prefix) ?? Number.MAX_SAFE_INTEGER
  return [...byPath.values()].sort((a, b) => {
    const len = Math.min(a.factorPath.length, b.factorPath.length)
    for (let i = 0; i < len; i++) {
      if (a.factorPath[i] === b.factorPath[i]) continue
      const parent = i > 0 ? a.factorPath[i - 1] : null
      const bySibling = compareFactorSiblings(a.factorPath[i], b.factorPath[i], parent)
      if (bySibling !== 0) return bySibling
      // Structural tie between distinct siblings: order by when each group first appeared.
      return seenRank(a.factorPath.slice(0, i + 1).join(' > ')) - seenRank(b.factorPath.slice(0, i + 1).join(' > '))
    }
    return a.factorPath.length - b.factorPath.length
  })
}
